import { BrowserRouter } from "react-router-dom";
import { ApiErrorDispatcher } from "@/api";
import { ToastProvider } from "@/components";
import { AppConfigProvider } from "@/config";
import { AdvancedModeProvider } from "@/context/AdvancedModeContext";
import { RoleProvider } from "@/role";
import { SessionProvider } from "@/session";
import { WorkspaceProvider } from "@/workspace";
import { AppRoutes } from "@/routes";

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      {/* AppConfigProvider is placed above SessionProvider so the mocked-deps
          banner doesn't wait on /api/session/me. The /api/config call is
          role-free (skipActingRole) so no ordering hazard with RoleProvider. */}
      <AppConfigProvider>
        <ToastProvider>
          {/* WorkspaceProvider sits alongside SessionProvider (not nested)
              because the two contexts are independent: SessionProvider
              owns /api/session/me (alias + grants); WorkspaceProvider
              owns /api/session (tenant descriptor for the Ring App Store
              integration). Either can be null without breaking the other.
              Placed above SessionProvider so the WorkspaceDisconnected
              modal (mounted in AdminLayout) can render even when the
              session has gone away. */}
          <WorkspaceProvider>
          <SessionProvider>
            {/* AdvancedModeProvider depends on useSession for alias-keyed
                prefs, so it nests inside SessionProvider. It's above
                RoleProvider so the sidebar (which reads Advanced Mode)
                and every page can consume the context. */}
            <AdvancedModeProvider>
              {/* RoleProvider depends on useSession + useLocation, so it must be
                  nested inside SessionProvider (and inside BrowserRouter). */}
              <RoleProvider>
                {/* ApiErrorDispatcher needs toasts + role (forcePickerOpen),
                    so it sits below both providers. Every page uses
                    useApiErrorHandler() — see DESIGN.md §10 + task 44. */}
                <ApiErrorDispatcher>
                  <AppRoutes />
                </ApiErrorDispatcher>
              </RoleProvider>
            </AdvancedModeProvider>
          </SessionProvider>
          </WorkspaceProvider>
        </ToastProvider>
      </AppConfigProvider>
    </BrowserRouter>
  );
}
